import { useState, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Alert,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Add as AddIcon, Refresh as RefreshIcon } from "@mui/icons-material";
import {
  useListAccessibleFirms,
  useCreateFirmOps,
  useUpdateFirmOps,
  type FirmResponse,
  type CreateFirmRequest,
  type UpdateFirmRequest,
  UserRole,
} from "@taxassist/api-client";
import { FirmTable } from "../../components/admin/FirmTable";
import { FirmDialog } from "../../components/admin/FirmDialog";
import { FirmDetailsDrawer } from "../../components/admin/FirmDetailsDrawer";
import { NotificationSnackbar } from "../../components/common/NotificationSnackbar";
import { useAuth } from "../../contexts/AuthContext";

export function FirmManagementPage() {
  const { user } = useAuth();
  const [selectedFirms, setSelectedFirms] = useState<Set<string>>(new Set());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedFirm, setSelectedFirm] = useState<FirmResponse | null>(null);
  const [viewedFirm, setViewedFirm] = useState<FirmResponse | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Check if user can modify firms (admin only - reviewers get read-only access)
  const canModifyFirms = user?.role === UserRole.admin;

  // API hooks
  const {
    data: firmsData,
    isLoading: firmsLoading,
    error: firmsError,
    refetch: refetchFirms,
  } = useListAccessibleFirms();

  // Handle both old (array) and new (object with firms array) response formats
  const firms = Array.isArray(firmsData)
    ? firmsData // Old format: direct array
    : firmsData?.firms || []; // New format: object with firms property

  const createFirmMutation = useCreateFirmOps({
    mutation: {
      onSuccess: () => {
        setSuccessMessage("Firm created successfully");
        refetchFirms();
      },
      onError: (error: unknown) => {
        setErrorMessage(
          error instanceof Error ? error.message : "Failed to create firm",
        );
      },
    },
  });

  const updateFirmMutation = useUpdateFirmOps({
    mutation: {
      onSuccess: () => {
        setSuccessMessage("Firm updated successfully");
        refetchFirms();
      },
      onError: (error: unknown) => {
        setErrorMessage(
          error instanceof Error ? error.message : "Failed to update firm",
        );
      },
    },
  });

  // Handlers - must be defined before any conditional returns
  const handleCreateFirm = useCallback(() => {
    setSelectedFirm(null);
    setDialogOpen(true);
  }, []);

  const handleEditFirm = useCallback((firm: FirmResponse) => {
    setSelectedFirm(firm);
    setDialogOpen(true);
  }, []);

  const handleViewFirm = useCallback((firm: FirmResponse) => {
    setViewedFirm(firm);
    setDrawerOpen(true);
  }, []);

  const handleToggleFirmStatus = useCallback(
    async (firm: FirmResponse) => {
      try {
        const updateData: UpdateFirmRequest = {
          name: firm.name,

          isActive: !firm.isActive,
          surePrepConfig: firm.surePrepConfig,
          metadata: firm.metadata,
        };

        await updateFirmMutation.mutateAsync({
          firmId: firm.firmId,
          data: updateData,
        });
      } catch {
        setErrorMessage(
          `Failed to ${firm.isActive ? "deactivate" : "activate"} firm`,
        );
      }
    },
    [updateFirmMutation],
  );

  const handleSaveFirm = useCallback(
    async (firmData: CreateFirmRequest | UpdateFirmRequest) => {
      if (selectedFirm) {
        // Update existing firm
        await updateFirmMutation.mutateAsync({
          firmId: selectedFirm.firmId,
          data: firmData as UpdateFirmRequest,
        });
      } else {
        // Create new firm
        await createFirmMutation.mutateAsync({
          data: firmData as CreateFirmRequest,
        });
      }
    },
    [selectedFirm, createFirmMutation, updateFirmMutation],
  );

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
    setSelectedFirm(null);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setDrawerOpen(false);
    setViewedFirm(null);
  }, []);

  const handleRefresh = useCallback(() => {
    refetchFirms();
  }, [refetchFirms]);

  // No need to check admin role here - AdminRoute handles that

  const isLoading =
    firmsLoading ||
    createFirmMutation.isPending ||
    updateFirmMutation.isPending;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      {/* Header - Similar to Jobs page */}
      <Box px={3} py={2}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {canModifyFirms && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateFirm}
                disabled={isLoading}
                size="small"
              >
                Create Firm
              </Button>
            )}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                {firms.length} total
              </Typography>
              <Typography
                variant="body2"
                color="success.main"
                sx={{ fontWeight: 500 }}
              >
                {firms.filter((f) => f.isActive).length} active
              </Typography>
              {firms.filter((f) => !f.isActive).length > 0 && (
                <Typography variant="body2" color="text.secondary">
                  {firms.filter((f) => !f.isActive).length} inactive
                </Typography>
              )}
              {selectedFirms.size > 0 && (
                <Typography
                  variant="body2"
                  color="primary.main"
                  sx={{ fontWeight: 500 }}
                >
                  {selectedFirms.size} selected
                </Typography>
              )}
            </Box>
          </Box>
          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh} disabled={isLoading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Main content area - Similar to Jobs page */}
      <Box sx={{ flex: 1, display: "flex" }}>
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          {/* Error Alert */}
          {firmsError && (
            <Alert severity="error" sx={{ mx: 3, mb: 2 }}>
              Failed to load firms: {firmsError.message}
            </Alert>
          )}

          <Box sx={{ flex: 1, px: 3, pb: 3 }}>
            <FirmTable
              firms={firms}
              isLoading={isLoading}
              onEditFirm={canModifyFirms ? handleEditFirm : undefined}
              onViewFirm={handleViewFirm}
              onToggleFirmStatus={
                canModifyFirms ? handleToggleFirmStatus : undefined
              }
              selectedFirms={selectedFirms}
              onSelectionChange={setSelectedFirms}
              canModifyFirms={canModifyFirms}
            />
          </Box>
        </Box>
      </Box>

      {/* Firm Dialog */}
      <FirmDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        firm={selectedFirm}
        onSave={handleSaveFirm}
        isLoading={isLoading}
      />

      {/* Firm Details Drawer */}
      <FirmDetailsDrawer
        open={drawerOpen}
        onClose={handleCloseDrawer}
        firm={viewedFirm}
      />

      {/* Success Snackbar */}
      <NotificationSnackbar
        message={successMessage}
        onClose={() => setSuccessMessage(null)}
        severity="success"
      />

      {/* Error Snackbar */}
      <NotificationSnackbar
        message={errorMessage}
        onClose={() => setErrorMessage(null)}
        severity="error"
      />
    </Box>
  );
}
